import { PatientSummaryManager } from "@ngt/opms";
import { JsonServiceClient } from "@servicestack/client";
import { useSnackbar } from "notistack";
import { useMemo } from "react";
import { IInstitution, IPatient, Patient, PatientStateType, WithdrawalStateType } from "../../../api/dtos";
import usePatientSummaryConsentActions from "./usePatientSummaryConsentActions";
import usePatientSummaryCreateAndNotifyActions from "./usePatientSummaryCreateAndNotifyActions";


const hideConsentActions = (actionPatient: IPatient | null | undefined) => 
    !actionPatient || 
    (
        actionPatient.patientStateId !== PatientStateType.ConsentPending && 
        actionPatient.patientStateId !== PatientStateType.Consent
    ) ||
    [WithdrawalStateType.FullyWithdrawn, WithdrawalStateType.WithdrawnProms].includes((actionPatient as Patient).withdrawalStateId as WithdrawalStateType);
    
const hideBaselineActions = (actionPatient: IPatient | null | undefined) => actionPatient != null;

const hidePostAllocationActions = (actionPatient: IPatient | null | undefined) => 
    !actionPatient || 
    (
        actionPatient.patientStateId !== PatientStateType.Allocated && 
        actionPatient.patientStateId !== PatientStateType.PostAllocated
    ) ||
    [WithdrawalStateType.FullyWithdrawn, WithdrawalStateType.WithdrawnProms].includes((actionPatient as Patient).withdrawalStateId as WithdrawalStateType);;

const hideThreeMonthsPostSurgeryActions = (actionPatient: IPatient | null | undefined) => 
    !actionPatient || 
    (
        actionPatient.patientStateId !== PatientStateType.PostAllocated && 
        actionPatient.patientStateId !== PatientStateType.ThreeMonthsPostSurgery
    ) ||
    [WithdrawalStateType.FullyWithdrawn, WithdrawalStateType.WithdrawnProms].includes((actionPatient as Patient).withdrawalStateId as WithdrawalStateType);;
    
const hideSixMonthsPostSurgeryActions = (actionPatient: IPatient | null | undefined) => 
    !actionPatient || 
    (
        actionPatient.patientStateId !== PatientStateType.ThreeMonthsPostSurgery && 
        actionPatient.patientStateId !== PatientStateType.SixMonthsPostSurgery
    ) ||
    [WithdrawalStateType.FullyWithdrawn, WithdrawalStateType.WithdrawnProms].includes((actionPatient as Patient).withdrawalStateId as WithdrawalStateType);;

const hideTwelveMonthsPostSurgeryActions = (actionPatient: IPatient | null | undefined) => 
    !actionPatient || 
    (
        actionPatient.patientStateId !== PatientStateType.SixMonthsPostSurgery && 
        actionPatient.patientStateId !== PatientStateType.TwelveMonthsPostSurgery
    ) ||
    [WithdrawalStateType.FullyWithdrawn, WithdrawalStateType.WithdrawnProms].includes((actionPatient as Patient).withdrawalStateId as WithdrawalStateType);;

const hideTwentyFourMonthsPostSurgeryActions = (actionPatient: IPatient | null | undefined) => 
    !actionPatient || 
    (
        actionPatient.patientStateId !== PatientStateType.TwelveMonthsPostSurgery && 
        actionPatient.patientStateId !== PatientStateType.TwentyFourMonthsPostSurgery
    ) ||
    [WithdrawalStateType.FullyWithdrawn, WithdrawalStateType.WithdrawnProms].includes((actionPatient as Patient).withdrawalStateId as WithdrawalStateType);;

const hideFiveYearsPostSurgeryActions = (actionPatient: IPatient | null | undefined) => 
    !actionPatient || 
    (
        actionPatient.patientStateId !== PatientStateType.TwentyFourMonthsPostSurgery && 
        actionPatient.patientStateId !== PatientStateType.FiveYearsPostSurgery
    ) ||
    [WithdrawalStateType.FullyWithdrawn, WithdrawalStateType.WithdrawnProms].includes((actionPatient as Patient).withdrawalStateId as WithdrawalStateType);;


const usePatientSummaryEventActions = (
    saving: boolean,
    updatePatientState: (state: PatientStateType, skipPatientEmail: boolean, patientStatusReason?: string) => void,
    enqueueSnackbar: ReturnType<typeof useSnackbar>['enqueueSnackbar'],
    client: JsonServiceClient,
    patient: IPatient | null | undefined,
    patientValid: boolean,
    institution: IInstitution | null | undefined,
    canUpdatePatient: boolean | undefined,
    manager: PatientSummaryManager | undefined
) => {
    const typedPatient = patient as Patient;
    const consentActions = usePatientSummaryConsentActions(PatientStateType.Consent, hideConsentActions, 'Consent', 'Consent', saving, updatePatientState, enqueueSnackbar, client, patient, patientValid, institution, canUpdatePatient, manager);
    const baselineActions = usePatientSummaryCreateAndNotifyActions(PatientStateType.RadiologistReview, hideBaselineActions, 'Baseline', 'Baseline', saving, updatePatientState, enqueueSnackbar, client, patient, patientValid, typedPatient?.fromKoreanSite);
    const postAllocationActions = usePatientSummaryCreateAndNotifyActions(PatientStateType.PostAllocated, hidePostAllocationActions, 'Post-allocation', 'Post-allocation', saving, updatePatientState, enqueueSnackbar, client, patient, patientValid, typedPatient?.fromKoreanSite);
    const threeMonthsPostSurgeryEventActions = usePatientSummaryCreateAndNotifyActions(PatientStateType.ThreeMonthsPostSurgery, hideThreeMonthsPostSurgeryActions, '3 Months Post Surgery', '3 months post surgery', saving, updatePatientState, enqueueSnackbar, client, patient, patientValid, typedPatient?.fromKoreanSite);
    const sixMonthsPostSurgeryEventActions = usePatientSummaryCreateAndNotifyActions(PatientStateType.SixMonthsPostSurgery, hideSixMonthsPostSurgeryActions, '6 Months Post Surgery', '6 months post surgery', saving, updatePatientState, enqueueSnackbar, client, patient, patientValid, typedPatient?.fromKoreanSite);
    const twelveMonthsPostSurgeryEventActions = usePatientSummaryCreateAndNotifyActions(PatientStateType.TwelveMonthsPostSurgery, hideTwelveMonthsPostSurgeryActions, '12 Months Post Surgery', '12 months post surgery', saving, updatePatientState, enqueueSnackbar, client, patient, patientValid, typedPatient?.fromKoreanSite);
    const twentyFourMonthsPostSurgeryEventActions = usePatientSummaryCreateAndNotifyActions(PatientStateType.TwentyFourMonthsPostSurgery, hideTwentyFourMonthsPostSurgeryActions, '24 Months Post Surgery', '24 months post surgery', saving, updatePatientState, enqueueSnackbar, client, patient, patientValid, typedPatient?.fromKoreanSite);
    const fiveYearsPostSurgeryEventActions = usePatientSummaryCreateAndNotifyActions(PatientStateType.FiveYearsPostSurgery, hideFiveYearsPostSurgeryActions, '5 Years Post Surgery', '5 years post surgery', saving, updatePatientState, enqueueSnackbar,  client, patient, patientValid, typedPatient?.fromKoreanSite);
    
    const eventActionMapping = useMemo(() => {
        return {
            'consent': consentActions,
            'baseline': baselineActions,
            'post-allocation': postAllocationActions,
            'three-months-post-surgery': threeMonthsPostSurgeryEventActions,
            'six-months-post-surgery': sixMonthsPostSurgeryEventActions,
            'twelve-months-post-surgery': twelveMonthsPostSurgeryEventActions,
            'twenty-four-months-post-surgery': twentyFourMonthsPostSurgeryEventActions,
            'five-years-post-surgery': fiveYearsPostSurgeryEventActions,
        };
    }, [ consentActions, baselineActions, postAllocationActions, threeMonthsPostSurgeryEventActions, sixMonthsPostSurgeryEventActions, twelveMonthsPostSurgeryEventActions, twentyFourMonthsPostSurgeryEventActions, fiveYearsPostSurgeryEventActions]);

    return eventActionMapping;
}

export default usePatientSummaryEventActions;